import {
    Box,
    Button,
    Container,
    Flex,
    Heading,
    HStack,
    IconButton,
    Image,
    SimpleGrid,
    Stack,
    Text,
    Dialog,
    Portal,
    Spinner,
    Select,
    createListCollection,
    Separator,
    Icon,
    VStack,
    Badge,
} from '@chakra-ui/react'
import { useEffect, useState, useRef, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { toaster } from '../components/ui/toaster'
import { LuDownload, LuLayoutGrid, LuList, LuMaximize2, LuPlay, LuPause, LuStar, LuTrash2, LuVideoOff, LuRotateCw, LuRectangleVertical, LuSparkles, LuUser, LuX, LuInfo, LuCloudUpload, LuCloudOff } from 'react-icons/lu'
import { toggleFavorite, deleteAllEventVideos, deleteVideoCompletely, backfillEventStream, updateVideoRotation, updateVideoForceVertical, retryStreamUpload, removeStreamCopy } from '../services/firebaseService'
import { fetchVideoAIConfig, retryVideoAnalysis } from '../services/videoAI'
import { enabledFeatureKeys } from '../constants/videoAI'
import { StreamPlayer } from '../components/ui/StreamPlayer'
import { videoDisplayStyle, forceVerticalAspect, nextRotation } from '../utils/videoDisplay'
import { ownerLabel, videosByOwner } from '../utils/videoOwner'
import { sortVideos, VIDEO_SORTS } from '../utils/videoSort'
import { isStreamReady, needsStreamCopy } from '../utils/streamCopy'
import { VideoInfoDialog } from '../components/VideoInfoDialog'
import { EmptyState } from '../components/ui/EmptyState'
import { DownloadButton } from '../components/ui/DownloadButton'
import { OverflowActionRow } from '../components/ui/OverflowActionRow'
import { useVideoDownload } from '../hooks/useVideoDownload'
import { Tooltip } from '../components/ui/tooltip'
import { StatusBadge } from '../components/ui/StatusBadge'
import { DisplayControlsToggle } from '../components/DisplayControlsToggle'
import { useVideoDisplayControls } from '../hooks/useVideoDisplayControls'
import { useTranslation } from '../i18n'
import { ExportDialog } from '../components/videoExport/ExportDialog'
import { ExportList } from '../components/videoExport/ExportList'
import { VideoAIBackfillStrip } from '../components/VideoAIBackfillProgress'
import { useVideoAIBackfill } from '../hooks/useVideoAIBackfill'

import { useDocumentData, useCollection } from '@kage1020/react-firebase-hooks/firestore';

import { doc, collection, query, where, orderBy } from 'firebase/firestore'
import { db } from '../firebase'


// The card actions that leave the row first when the card is too narrow.
const OVERFLOW_FIRST = ['download', 'reanalyze']

const VideoCard = ({ video, viewMode, onOpenModal, onOpenInfo, onDelete, showDisplayControls, canReanalyze, canRemoveStreamCopy }) => {
    const { t } = useTranslation()
    const [reanalyzing, setReanalyzing] = useState(false)
    const [removingCopy, setRemovingCopy] = useState(false)

    // Sysadmin test tool: undone by "Send N to Cloudflare", so no confirmation.
    const handleRemoveStreamCopy = async () => {
        setRemovingCopy(true)
        try {
            await removeStreamCopy(video.id)
            toaster.create({ title: t('videosPage.streamCopyRemoved'), type: 'success' })
        } catch (err) {
            toaster.create({ title: t('videosPage.streamCopyRemoveFailed'), description: err.message, type: 'error' })
        } finally {
            setRemovingCopy(false)
        }
    }
    const videoRef = useRef(null)
    const [duration, setDuration] = useState(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const tStart = video.trimStart || 0
    const tEnd = video.trimEnd

    // Re-run the AI analysis for this one clip. The callable deletes whatever
    // analysis exists first, which is what gets past runAnalysis's create()
    // double-billing guard.
    const handleReanalyze = async () => {
        setReanalyzing(true)
        try {
            await retryVideoAnalysis(video.id)
            toaster.create({ title: t('videosPage.reanalyzeStarted'), type: 'success' })
        } catch (err) {
            toaster.create({ title: t('videosPage.reanalyzeFailed'), description: err.message, type: 'error' })
        } finally {
            setReanalyzing(false)
        }
    }

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            const effectiveDuration = tEnd ? (tEnd - tStart) : (videoRef.current.duration - tStart)
            setDuration(effectiveDuration)
            if (tStart > 0) videoRef.current.currentTime = tStart
        }
    }

    // Enforce trim loop
    useEffect(() => {
        const v = videoRef.current
        if (!v) return
        const onTimeUpdate = () => {
            if (tEnd && v.currentTime >= tEnd) v.currentTime = tStart
            if (v.currentTime < tStart) v.currentTime = tStart
        }
        v.addEventListener('timeupdate', onTimeUpdate)
        return () => v.removeEventListener('timeupdate', onTimeUpdate)
    }, [tStart, tEnd])

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
            } else {
                videoRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }

    const download = useVideoDownload({
        source: isStreamReady(video) ? { kind: 'video', id: video.id } : undefined,
        url: video.url || video.videoUrl,
        fileName: `video-${video.id}.mp4`,
    })

    // In display order. What does not fit the card moves into the ⋯ menu,
    // download and re-analyze first (OVERFLOW_FIRST).
    const actions = [
        !isStreamReady(video) && {
            key: 'play',
            label: isPlaying ? t('videosPage.pause') : t('videosPage.play'),
            icon: isPlaying ? <LuPause /> : <LuPlay />,
            onClick: togglePlay,
        },
        { key: 'expand', label: t('videosPage.openViewer'), icon: <LuMaximize2 />, onClick: () => onOpenModal(video) },
        { key: 'info', label: t('videosPage.moreInfo'), icon: <LuInfo />, onClick: () => onOpenInfo(video) },
        {
            key: 'download',
            label: t('videosPage.downloadVideo'),
            tooltip: download.status === 'preparing' ? t('common.preparingDownload') : undefined,
            icon: <LuDownload />,
            loading: download.busy,
            onClick: download.start,
        },
        canReanalyze && {
            key: 'reanalyze',
            label: t('videosPage.reanalyze'),
            icon: <LuSparkles />,
            loading: reanalyzing,
            onClick: handleReanalyze,
        },
        {
            key: 'favorite',
            label: video.isFavorite ? t('videosPage.unfavorite') : t('videosPage.favorite'),
            icon: <LuStar fill={video.isFavorite ? 'currentColor' : 'none'} />,
            color: video.isFavorite ? 'yellow.400' : undefined,
            onClick: () => toggleFavorite(video.id, !video.isFavorite),
        },
        // Rotation / force-vertical drive the desktop playback app, which
        // reads the Firebase original — so they stay available even when the
        // card previews the Cloudflare copy (whose iframe can't be rotated),
        // and the button itself carries the current state.
        showDisplayControls && {
            key: 'rotate',
            label: t('videosPage.rotate'),
            icon: <LuRotateCw />,
            color: video.rotation ? 'blue.400' : undefined,
            onClick: () => updateVideoRotation(video.id, nextRotation(video.rotation)),
        },
        showDisplayControls && {
            key: 'forceVertical',
            label: video.forceVertical ? t('videosPage.unforceVertical') : t('videosPage.forceVertical'),
            icon: <LuRectangleVertical />,
            color: video.forceVertical ? 'blue.400' : undefined,
            onClick: () => updateVideoForceVertical(video.id, !video.forceVertical),
        },
        canRemoveStreamCopy && !!video.cf?.uid && {
            key: 'removeStreamCopy',
            label: t('videosPage.removeStreamCopy'),
            icon: <LuCloudOff />,
            loading: removingCopy,
            menuOnly: true,
            onClick: handleRemoveStreamCopy,
        },
        // Only event admins and sysadmins get onDelete.
        onDelete && {
            key: 'delete',
            label: t('videosPage.deleteVideo'),
            icon: <LuTrash2 />,
            danger: true,
            onClick: () => onDelete(video),
        },
    ].filter(Boolean)

    const formatTime = (seconds) => {
        if (!seconds) return '00:00'
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`
    }

    const formatDate = (timestamp) => {
        if (!timestamp) return ''
        return new Date(timestamp.seconds * 1000).toLocaleString()
    }

    return (
        <Box
            borderWidth="1px"
            borderRadius="lg"
            overflow="hidden"
            bg="bg.surface"
            borderColor="border.subtle"
            position="relative"
            width="100%"
            height={viewMode === 'grid' ? 'auto' : '120px'}
            display={viewMode === 'list' ? 'flex' : 'block'}
        >
            <Box
                position="relative"
                width={viewMode === 'list' ? '200px' : '100%'}
                height={viewMode === 'list' ? '100%' : '200px'}
                aspectRatio={forceVerticalAspect(video.forceVertical)}
                bg="black"
            >
                {isStreamReady(video) ? (
                    <StreamPlayer cf={video.cf} controls fill />
                ) : (
                    <>
                        <video
                            ref={videoRef}
                            src={video.url || video.videoUrl}
                            style={{ width: '100%', height: '100%', ...videoDisplayStyle(video.rotation, video.forceVertical) }}
                            onLoadedMetadata={handleLoadedMetadata}
                            onClick={togglePlay}
                        />
                        <Flex
                            position="absolute"
                            inset={0}
                            align="center"
                            justify="center"
                            opacity={isPlaying ? 0 : 1}
                            transition="opacity 0.2s"
                            bg="blackAlpha.400"
                            pointerEvents="none"
                        >
                            <Icon as={LuPlay} boxSize={8} color="white" />
                        </Flex>
                        <Flex position="absolute" bottom={1} right={1} bg="blackAlpha.700" px={1} borderRadius="sm">
                            <Text fontSize="xs" color="white">{formatTime(duration)}</Text>
                        </Flex>
                    </>
                )}
            </Box>

            <Box p={4} flex="1" minW={0}>
                <Stack justify="space-between" h="100%">
                    <Box>
                        <HStack justify="space-between">
                            <Text fontSize="sm" color="fg.muted">{formatDate(video.createdAt)}</Text>
                            <StatusBadge status={video.accepted ? "accepted" : video.isModerated ? "rejected" : "pending"} />
                        </HStack>
                        {video.cf?.status === 'processing' && (
                            <Text fontSize="xs" color="fg.muted" mt={1}>{t('videosPage.streamProcessing')}</Text>
                        )}
                        {video.cf?.status === 'removed' && (
                            <Text fontSize="xs" color="fg.muted" mt={1}>{t('videosPage.streamRemoved')}</Text>
                        )}
                        {video.cf?.status === 'error' && (
                            <HStack mt={1} gap={2} wrap="wrap">
                                <Text fontSize="xs" color="red.400">{t('videosPage.streamError')}</Text>
                                <Button
                                    size="xs"
                                    variant="outline"
                                    maxW="100%"
                                    title={t('videosPage.streamRetry')}
                                    onClick={async () => {
                                        try {
                                            await retryStreamUpload({ kind: 'video', id: video.id })
                                            toaster.create({ title: t('videosPage.streamRetryQueued'), status: 'success' })
                                        } catch (err) {
                                            toaster.create({ title: t('videosPage.streamRetryFailed'), description: err.message, status: 'error' })
                                        }
                                    }}
                                >
                                    {/* Truncated rather than wider than a narrow card. */}
                                    <Text as="span" truncate>{t('videosPage.streamRetry')}</Text>
                                </Button>
                            </HStack>
                        )}
                    </Box>
                    <OverflowActionRow actions={actions} overflowOrder={OVERFLOW_FIRST} moreLabel={t('videosPage.moreActions')} />
                </Stack>
            </Box>
        </Box>
    )
}

export default function EventVideosPage() {
    const { eventId } = useParams()
    const { isAdmin, isSysAdmin, getEventRole } = useAuth()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { canControl, enabled: displayControlsEnabled, setEnabled: setDisplayControlsEnabled, active: showDisplayControls } = useVideoDisplayControls(eventId)

    const PAGE_SIZES = useMemo(() => createListCollection({
        items: [
            { label: t('videosPage.perPage', { n: 10 }), value: '10' },
            { label: t('videosPage.perPage', { n: 20 }), value: '20' },
            { label: t('videosPage.perPage', { n: 50 }), value: '50' },
            { label: t('videosPage.pageSizeAll'), value: 'all' },
        ],
    }), [t])

    const [viewMode, setViewMode] = useState('grid') // 'grid' | 'list'
    const [pageSize, setPageSize] = useState(['20'])
    const [visibleCount, setVisibleCount] = useState(20)
    const [sort, setSort] = useState(['createdDesc'])

    const SORTS = useMemo(() => {
        const labels = {
            createdDesc: t('videosPage.sortCreatedDesc'),
            createdAsc: t('videosPage.sortCreatedAsc'),
            durationDesc: t('videosPage.sortDurationDesc'),
            durationAsc: t('videosPage.sortDurationAsc'),
            challenge: t('videosPage.sortChallenge'),
        }
        return createListCollection({ items: VIDEO_SORTS.map((value) => ({ value, label: labels[value] })) })
    }, [t])

    // Same gate as the `deleteVideo` callable: sysadmin or this event's admin.
    const canDeleteVideos = isSysAdmin || getEventRole(eventId) === 'eventadmin'

    // A running "Analyze existing videos" job, shown above the grid. Only a
    // sysadmin can start or read one.
    const backfill = useVideoAIBackfill(eventId, isSysAdmin)

    const [event] = useDocumentData(doc(db, 'events', eventId));

    // Real-time videos listener
    const videosQuery = query(
        collection(db, 'videos'),
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc')
    )
    const [videosSnapshot, loadingVideos] = useCollection(videosQuery)

    const eventVideos = videosSnapshot?.docs.map(d => ({ id: d.id, ...d.data() })) || []
    // `?uid=` narrows the page to one participant (linked from the user page);
    // without it, nothing changes.
    const [searchParams, setSearchParams] = useSearchParams()
    const ownerUid = searchParams.get('uid') || null
    const allVideos = sortVideos(videosByOwner(eventVideos, ownerUid), sort[0])
    const videos = pageSize[0] === 'all' ? allVideos : allVideos.slice(0, visibleCount)
    const hasMore = pageSize[0] !== 'all' && visibleCount < allVideos.length
    const clearOwner = () => setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('uid')
        return next
    })

    useEffect(() => {
        if (!isAdmin) {
            navigate('/', { replace: true })
        }
    }, [isAdmin, navigate])

    const handlePageSizeChange = (value) => {
        setPageSize(value)
        const newSize = value[0] === 'all' ? Infinity : parseInt(value[0])
        setVisibleCount(newSize)
    }

    const handleLoadMore = () => {
        const size = parseInt(pageSize[0]) || 20
        setVisibleCount(prev => prev + size)
    }

    // Modal State
    // The re-analyze button only makes sense when the event actually runs
    // analysis; a sysadmin on an AI-less event should not see it.
    const [aiEnabled, setAiEnabled] = useState(false)
    useEffect(() => {
        if (!eventId) return
        let cancelled = false
        fetchVideoAIConfig(eventId)
            .then((c) => { if (!cancelled) setAiEnabled(c.enabled === true && enabledFeatureKeys(c).length > 0) })
            .catch((err) => {
                // A silent catch here hides a denied read as "AI is off", which
                // is indistinguishable from the button simply not applying.
                console.warn('Could not read the event AI config:', err.message)
                if (!cancelled) setAiEnabled(false)
            })
        return () => { cancelled = true }
    }, [eventId])

    const [selectedVideo, setSelectedVideo] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Delete all state
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    // Export state
    const [isExportOpen, setIsExportOpen] = useState(false)
    // The export is of the whole event even while the page shows one person.
    const acceptedCount = eventVideos.filter((v) => v.accepted === true).length

    // Accepted videos the Cloudflare sync never copied, or whose copy failed.
    // Counted over the whole event, like the backfill that sends them.
    const missingStreamCount = event?.streamEnabled === true
        ? eventVideos.filter((v) => needsStreamCopy(v, { includeErrors: true })).length
        : 0
    const [isSendingToStream, setIsSendingToStream] = useState(false)
    const handleSendToStream = async () => {
        setIsSendingToStream(true)
        try {
            const { sent, remaining } = await backfillEventStream(eventId, { includeErrors: true })
            toaster.create({
                title: t('videosPage.sendToStreamDone', { count: sent }),
                description: remaining > 0 ? t('videosPage.sendToStreamRemaining', { count: remaining }) : undefined,
                type: 'success',
            })
        } catch (error) {
            console.error('Failed to send videos to Cloudflare:', error)
            toaster.create({ title: t('videosPage.sendToStreamFailed'), description: error.message, type: 'error' })
        } finally {
            setIsSendingToStream(false)
        }
    }

    const handleDeleteAll = async () => {
        setIsDeleting(true)
        try {
            const count = await deleteAllEventVideos(eventId)
            toaster.create({ title: t('videosPage.deleted', { count }), type: 'success' })
        } catch (error) {
            console.error('Failed to delete videos:', error)
            toaster.create({ title: t('videosPage.deleteFailed'), type: 'error' })
        } finally {
            setIsDeleting(false)
            setIsDeleteDialogOpen(false)
        }
    }

    const [infoVideo, setInfoVideo] = useState(null)
    const [isInfoOpen, setIsInfoOpen] = useState(false)
    const openInfo = (video) => {
        setInfoVideo(video)
        setIsInfoOpen(true)
    }

    // One video, deleted everywhere (see deleteVideoCompletely).
    const [videoToDelete, setVideoToDelete] = useState(null)
    const [isDeletingVideo, setIsDeletingVideo] = useState(false)
    const handleDeleteVideo = async () => {
        if (!videoToDelete) return
        setIsDeletingVideo(true)
        try {
            await deleteVideoCompletely(videoToDelete.id)
            toaster.create({ title: t('videosPage.videoDeleted'), type: 'success' })
            setVideoToDelete(null)
        } catch (error) {
            console.error('Failed to delete video:', error)
            toaster.create({ title: t('videosPage.videoDeleteFailed'), description: error.message, type: 'error' })
        } finally {
            setIsDeletingVideo(false)
        }
    }
    const onDelete = canDeleteVideos ? setVideoToDelete : null

    const openModal = (video) => {
        setSelectedVideo(video)
        setIsModalOpen(true)
    }

    if (!event) return <Container py={10}><Spinner /></Container>

    return (
        <Container maxW="none" px={0} py={0}>
            <Stack gap={{ base: 4, lg: 6 }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Box>
                        <Heading size={{ base: 'md', md: 'lg' }} srOnly={{ base: true, lg: false }}>{t('videosPage.titleSuffix', { name: event.name })}</Heading>
                        <Text color="fg.muted">{t('videosPage.countSuffix', { count: allVideos.length })}</Text>
                    </Box>
                    <HStack gap={4} wrap="wrap">
                        <Select.Root
                            collection={SORTS}
                            value={sort}
                            onValueChange={(e) => setSort(e.value)}
                            size="sm"
                            width="220px"
                        >
                            <Select.Trigger aria-label={t('videosPage.sortLabel')}>
                                <Select.ValueText placeholder={t('videosPage.sortLabel')} />
                            </Select.Trigger>
                            <Select.Content>
                                {SORTS.items.map((item) => (
                                    <Select.Item item={item} key={item.value}>
                                        {item.label}
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Root>

                        <Select.Root
                            collection={PAGE_SIZES}
                            value={pageSize}
                            onValueChange={(e) => handlePageSizeChange(e.value)}
                            size="sm"
                            width="140px"
                        >
                            <Select.Trigger>
                                <Select.ValueText placeholder={t('videosPage.selectPageSize')} />
                            </Select.Trigger>
                            <Select.Content>
                                {PAGE_SIZES.items.map((item) => (
                                    <Select.Item item={item} key={item.value}>
                                        {item.label}
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Root>

                        <HStack gap={1} bg="bg.subtle" p={1} borderRadius="md">
                            <IconButton
                                size="sm"
                                variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                                onClick={() => setViewMode('grid')}
                                aria-label={t('videosPage.gridView')}
                            >
                                <LuLayoutGrid />
                            </IconButton>
                            <IconButton
                                size="sm"
                                variant={viewMode === 'list' ? 'solid' : 'ghost'}
                                onClick={() => setViewMode('list')}
                                aria-label={t('videosPage.listView')}
                            >
                                <LuList />
                            </IconButton>
                        </HStack>

                        {canControl && (
                            <DisplayControlsToggle
                                enabled={displayControlsEnabled}
                                onChange={setDisplayControlsEnabled}
                            />
                        )}

                        <Button colorPalette="brand" onClick={() => setIsExportOpen(true)} disabled={acceptedCount === 0} size="sm">
                            <LuDownload /> {t('videosPage.exportVideos')}
                        </Button>

                        {/* Event-wide, so hidden under a filter like Delete all. */}
                        {canDeleteVideos && !ownerUid && missingStreamCount > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                loading={isSendingToStream}
                                onClick={handleSendToStream}
                            >
                                <LuCloudUpload /> {t('videosPage.sendToStream', { count: missingStreamCount })}
                            </Button>
                        )}

                        {/* Hidden under a filter: it would read as "delete this
                            person's videos" while wiping the whole event. */}
                        {isSysAdmin && !ownerUid && (
                            <Button
                                colorPalette="red"
                                variant="outline"
                                size="sm"
                                disabled={eventVideos.length === 0}
                                onClick={() => setIsDeleteDialogOpen(true)}
                            >
                                <LuTrash2 /> {t('videosPage.deleteAll')}
                            </Button>
                        )}
                    </HStack>
                </Flex>

                <Separator />

                {ownerUid && (
                    <HStack
                        gap={2}
                        alignSelf="flex-start"
                        ps={3}
                        pe={1}
                        py={1}
                        borderRadius="md"
                        bg="var(--mc-bg-selected)"
                        border="1px solid"
                        borderColor="var(--mc-border)"
                    >
                        <LuUser />
                        <Text fontSize="sm" color="var(--mc-text)">
                            {t('videosPage.ownerFilter', {
                                name: ownerLabel(allVideos, ownerUid),
                                shown: allVideos.length,
                                total: eventVideos.length,
                            })}
                        </Text>
                        <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={t('videosPage.ownerFilterClear')}
                            title={t('videosPage.ownerFilterClear')}
                            onClick={clearOwner}
                        >
                            <LuX />
                        </IconButton>
                    </HStack>
                )}

                <VideoAIBackfillStrip eventId={eventId} job={backfill} />
                <ExportList eventId={eventId} />

                {loadingVideos ? (
                    <Flex justify="center" py={10}>
                        <Spinner size="xl" />
                    </Flex>
                ) : (
                    <>
                        {viewMode === 'grid' ? (
                            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={4}>
                                {videos.map(video => (
                                    <VideoCard key={video.id} video={video} viewMode="grid" onOpenModal={openModal} onOpenInfo={openInfo} onDelete={onDelete} showDisplayControls={showDisplayControls} canReanalyze={isSysAdmin && aiEnabled} canRemoveStreamCopy={isSysAdmin} />
                                ))}
                            </SimpleGrid>
                        ) : (
                            <Stack gap={4}>
                                {videos.map(video => (
                                    <VideoCard key={video.id} video={video} viewMode="list" onOpenModal={openModal} onOpenInfo={openInfo} onDelete={onDelete} showDisplayControls={showDisplayControls} canReanalyze={isSysAdmin && aiEnabled} canRemoveStreamCopy={isSysAdmin} />
                                ))}
                            </Stack>
                        )}

                        {videos.length === 0 && (
                            <EmptyState icon={LuVideoOff} message={t('videosPage.noVideos')} />
                        )}

                        {hasMore && (
                            <Flex justify="center" pt={4}>
                                <Button onClick={handleLoadMore} variant="outline">
                                    {t('common.loadMore')}
                                </Button>
                            </Flex>
                        )}
                    </>
                )}
            </Stack>

            <Dialog.Root open={isModalOpen} onOpenChange={(e) => setIsModalOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content maxW={{ base: '95vw', md: '800px' }} bg="black" p={0} overflow="hidden">
                            <Dialog.CloseTrigger color="white" top={2} right={2} />
                            <Dialog.Body p={0}>
                                {selectedVideo && (
                                    isStreamReady(selectedVideo) ? (
                                        <StreamPlayer
                                            cf={selectedVideo.cf}
                                            controls
                                            autoPlay
                                            aspectRatio={forceVerticalAspect(selectedVideo.forceVertical) || '16 / 9'}
                                            maxHeight="80vh"
                                        />
                                    ) : (
                                        <video
                                            src={selectedVideo.url || selectedVideo.videoUrl}
                                            controls
                                            autoPlay
                                            style={{ width: '100%', maxHeight: '80vh', ...videoDisplayStyle(selectedVideo.rotation, selectedVideo.forceVertical) }}
                                        />
                                    )
                                )}
                            </Dialog.Body>
                            {selectedVideo && (
                                <Box p={4} bg="bg.surface">
                                    <Flex justify="space-between" align="center">
                                        <Text fontSize="sm" color="fg.muted">
                                            {new Date(selectedVideo.createdAt?.seconds * 1000).toLocaleString()}
                                        </Text>
                                        <DownloadButton
                                            size="sm"
                                            source={isStreamReady(selectedVideo) ? { kind: 'video', id: selectedVideo.id } : undefined}
                                            url={selectedVideo.url || selectedVideo.videoUrl}
                                            fileName={`video-${selectedVideo.id}.mp4`}
                                        />
                                    </Flex>
                                </Box>
                            )}
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <Dialog.Root open={isDeleteDialogOpen} onOpenChange={(e) => setIsDeleteDialogOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content>
                            <Dialog.Header>
                                <Dialog.Title>{t('videosPage.deleteAllTitle')}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Text>
                                    {t('videosPage.deleteAllConfirm', { count: eventVideos.length })}
                                </Text>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <HStack gap={3}>
                                    <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
                                        {t('common.cancel')}
                                    </Button>
                                    <Button colorPalette="red" onClick={handleDeleteAll} loading={isDeleting}>
                                        {t('videosPage.deleteAllButton')}
                                    </Button>
                                </HStack>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <VideoInfoDialog
                video={infoVideo}
                eventId={eventId}
                open={isInfoOpen}
                onOpenChange={(e) => setIsInfoOpen(e.open)}
            />

            <Dialog.Root
                open={!!videoToDelete}
                onOpenChange={(e) => { if (!e.open && !isDeletingVideo) setVideoToDelete(null) }}
            >
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content>
                            <Dialog.Header>
                                <Dialog.Title>{t('videosPage.deleteVideoTitle')}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Text>{t('videosPage.deleteVideoConfirm')}</Text>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <HStack gap={3}>
                                    <Button variant="outline" onClick={() => setVideoToDelete(null)} disabled={isDeletingVideo}>
                                        {t('common.cancel')}
                                    </Button>
                                    <Button colorPalette="red" onClick={handleDeleteVideo} loading={isDeletingVideo}>
                                        {t('videosPage.deleteVideoButton')}
                                    </Button>
                                </HStack>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <ExportDialog
                open={isExportOpen}
                onOpenChange={(e) => setIsExportOpen(e.open)}
                eventId={eventId}
                videoCount={acceptedCount}
            />

        </Container>
    )
}
